import React, { useState } from "react";
import { useGetAdminWorkflowsQuery } from "@/store/admin_api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import OrganisationCombobox from "@/components/pages/platform-admin/organisations/OrganisationCombobox";
import Organisation from "@/model/interface/Organisation";
import InstallSystemWorkflowsDialog from "@/components/pages/platform-admin/activity/InstallSystemWorkflowsDialog";

const UNFILTERED_SIZE = 5;
const FILTERED_SIZE = 10;

export function WorkflowsTab() {
  const [page, setPage] = useState(0);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [installOpen, setInstallOpen] = useState(false);
  const { data: workflows, isLoading } = useGetAdminWorkflowsQuery({
    organisationId: organisation?.id,
    page,
    size: organisation ? FILTERED_SIZE : UNFILTERED_SIZE,
  });

  function selectOrganisation(selected: Organisation | null) {
    setOrganisation(selected);
    setPage(0);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflows</CardTitle>
        <CardDescription>
          {organisation
            ? `Workflows for ${organisation.name}, newest first.`
            : "The five most recent workflows across all organisations. Pick an organisation to see all of theirs."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <OrganisationCombobox value={organisation} onChange={selectOrganisation} />
          {organisation && (
            <Button variant="ghost" size="sm" onClick={() => selectOrganisation(null)}>
              Clear
            </Button>
          )}
          <Button variant="outline" className="ml-auto" onClick={() => setInstallOpen(true)}>
            Install system workflows
          </Button>
        </div>

        {isLoading && <Skeleton className="h-96 w-full" />}
        {!isLoading && (!workflows || workflows.items.length === 0) ? (
          <p className="text-sm text-muted-foreground">No workflows yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organisation</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(workflows?.items ?? []).map((workflow) => (
                <TableRow key={workflow.id}>
                  <TableCell className="font-medium">{workflow.organisation?.name ?? "—"}</TableCell>
                  <TableCell>{workflow.name}</TableCell>
                  <TableCell className="text-muted-foreground">{workflow.triggerType}</TableCell>
                  <TableCell>
                    <Badge variant={workflow.status === "ACTIVE" ? "default" : "secondary"}>{workflow.status}</Badge>
                  </TableCell>
                  <TableCell>{workflow.actions?.length ?? 0}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {workflow.createdAt ? format(new Date(workflow.createdAt), "MMM dd, yyyy") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {organisation && workflows && workflows.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {workflows.page + 1} of {workflows.totalPages}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={workflows.last} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <InstallSystemWorkflowsDialog open={installOpen} onOpenChange={setInstallOpen} />
    </Card>
  );
}
